import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useMe, useProjects } from './lib/queries'
import { AppShell } from './components/AppShell'
import { AcceptInvitePage, ForgotPage, LoginPage, ResetPage } from './pages/auth'
import { BoardPage } from './pages/BoardPage'
import { MyTasksPage } from './pages/MyTasksPage'
import { SettingsPage } from './pages/SettingsPage'
import { AdminPage } from './pages/AdminPage'
import { ProjectSettingsPage } from './pages/ProjectSettingsPage'
import { Spinner } from './components/ui'

export function App() {
  const me = useMe()
  const location = useLocation()

  if (me.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="h-5 w-5 text-[var(--ink-3)]" />
      </div>
    )
  }

  const user = me.data?.user

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot" element={<ForgotPage />} />
        <Route path="/reset/:token" element={<ResetPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        {/* Remember where they were headed so signing in lands them there. */}
        <Route
          path="*"
          element={<Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />}
        />
      </Routes>
    )
  }

  return (
    <AppShell user={user}>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/my" element={<MyTasksPage user={user} />} />
        <Route path="/p/:key" element={<BoardPage user={user} />} />
        <Route path="/p/:key/t/:taskKey" element={<BoardPage user={user} />} />
        <Route path="/p/:key/settings" element={<ProjectSettingsPage user={user} />} />
        <Route path="/settings" element={<SettingsPage user={user} />} />
        {user.role === 'admin' && <Route path="/admin" element={<AdminPage />} />}
        {/* An invite or reset link opened while already signed in should not
            dead-end; the board is the useful destination. */}
        <Route path="*" element={<Navigate to="/my" replace />} />
      </Routes>
    </AppShell>
  )
}

/** Sends people to their first board, or to My Tasks if they have none yet. */
function HomeRedirect() {
  const projects = useProjects()

  if (projects.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="h-5 w-5 text-[var(--ink-3)]" />
      </div>
    )
  }

  const first = projects.data?.find((p) => !p.archivedAt)
  return <Navigate to={first ? `/p/${first.key}` : '/my'} replace />
}
